package com.evoapps.evoshift.ui.calendar

import androidx.compose.foundation.border
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isAltPressed
import androidx.compose.ui.input.key.isCtrlPressed
import androidx.compose.ui.input.key.isMetaPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.widgets.LocalTopBarSelection
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.month_calendar_label
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.minusMonth
import kotlinx.datetime.plus
import kotlinx.datetime.plusMonth
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Duration.Companion.seconds
import kotlin.time.TimeSource

/*
 * SH-161 (F2): a month calendar by keyboard, as the roster grid has it (SH-169). The calendar is
 * one focus stop (its days are not Tab stops of their own): the arrows move a focus ring between
 * days and weeks, Home and End to the start and end of the week, Page Up and Page Down step the
 * top bar's month, Enter opens the day. A pointer press hides the ring until the next key.
 */

/** Test tags of a keyboard month calendar. */
object MonthCalendarTags {
    const val CALENDAR = "month_calendar"
    const val FOCUS_RING = "month_calendar_focus_ring"
    fun day(date: LocalDate) = "month_calendar_day_$date"
}

/** The calendar's keyboard state: the day the keyboard is on and whether its ring shows. */
@Stable
class MonthCalendarState {
    /** The day the keyboard is on (also the last day clicked). */
    var cursor by mutableStateOf<LocalDate?>(null)

    /** The keyboard was used last (the ring shows); a pointer press hides it again. */
    var keyboardActive by mutableStateOf(false)

    /** The calendar has the focus. */
    var focused by mutableStateOf(false)
        internal set

    internal val focusRequester = FocusRequester()

    /** The focus ring shows on [cursor]. */
    val showsRing: Boolean get() = focused && keyboardActive

    /** Whether [date]'s ring shows. */
    fun ringOn(date: LocalDate): Boolean = showsRing && cursor == date
}

@Composable
fun rememberMonthCalendarState(): MonthCalendarState = remember { MonthCalendarState() }

/**
 * Page Up / Page Down that opened another screen's calendar (a roster's month is a screen of its
 * own, a month without a published roster has no calendar): the calendar shown next takes the
 * focus on the same day of the month, so the keys go on there.
 */
internal object MonthCalendarFocus {
    private var request: Pair<Int, TimeSource.Monotonic.ValueTimeMark>? = null

    fun request(day: Int) {
        request = day to TimeSource.Monotonic.markNow()
    }

    fun clear() {
        request = null
    }

    /** The day of a request of the last few seconds (one where no calendar followed is forgotten). */
    fun take(): Int? = request?.takeIf { it.second.elapsedNow() < 10.seconds }?.first.also { request = null }
}

/**
 * Where [key] moves the keyboard from [from] in a month of [dates] (in order, the 1st first):
 * a day or a week along, the start or end of [from]'s week (weeks start on [firstDayOfWeek],
 * ISO 1 = Monday), never out of the month. Null: not a key that moves.
 */
internal fun stepCalendarDay(dates: List<LocalDate>, from: LocalDate, key: Key, firstDayOfWeek: Int): LocalDate? {
    if (dates.isEmpty()) return null
    val first = dates.first()
    val last = dates.last()
    val intoWeek = (from.dayOfWeek.isoDayNumber - firstDayOfWeek + 7) % 7
    val next = when (key) {
        Key.DirectionRight -> from.plus(DatePeriod(days = 1))
        Key.DirectionLeft -> from.minus(DatePeriod(days = 1))
        Key.DirectionDown -> from.plus(DatePeriod(days = 7))
        Key.DirectionUp -> from.minus(DatePeriod(days = 7))
        Key.MoveHome -> from.minus(DatePeriod(days = intoWeek))
        Key.MoveEnd -> from.plus(DatePeriod(days = 6 - intoWeek))
        else -> return null
    }
    return next.coerceIn(first, last)
}

/**
 * The calendar's keys and its single focus stop: on the box holding the month's [dates].
 * [startDate] is where the keyboard starts (today in this month, else the 1st); [onOpen] is Enter.
 * The days themselves take [monthCalendarDay].
 */
@Composable
fun Modifier.monthCalendarKeys(
    state: MonthCalendarState,
    dates: List<LocalDate>,
    firstDayOfWeek: Int,
    startDate: LocalDate?,
    onOpen: (LocalDate) -> Unit
): Modifier {
    val selection = LocalTopBarSelection.current
    val label = stringResource(Res.string.month_calendar_label)
    val current by rememberUpdatedState(onOpen)
    val currentDates by rememberUpdatedState(dates)
    val start = startDate?.takeIf { it in dates } ?: dates.firstOrNull()
    val currentStart by rememberUpdatedState(start)
    // Another month: the keyboard keeps its day of the month.
    LaunchedEffect(dates) {
        val cursor = state.cursor
        if (cursor != null && cursor !in dates && dates.isNotEmpty()) {
            state.cursor = dates[(cursor.day - 1).coerceIn(0, dates.lastIndex)]
        }
        if (state.focused) MonthCalendarFocus.clear()
    }
    // Reached with Page Up / Page Down from another calendar: the focus comes back here.
    LaunchedEffect(Unit) {
        val day = MonthCalendarFocus.take() ?: return@LaunchedEffect
        if (dates.isNotEmpty()) state.cursor = dates[(day - 1).coerceIn(0, dates.lastIndex)]
        runCatching { state.focusRequester.requestFocus() }
    }
    return this
        .pointerInput(state) {
            awaitPointerEventScope {
                while (true) {
                    val event = awaitPointerEvent(PointerEventPass.Initial)
                    if (event.type == PointerEventType.Press) state.keyboardActive = false
                }
            }
        }
        .onKeyEvent { event ->
            if (event.type != KeyEventType.KeyDown) return@onKeyEvent false
            // Ctrl / Cmd / Alt + a key is a shortcut (the months, the tabs): not the calendar's.
            if (event.isCtrlPressed || event.isMetaPressed || event.isAltPressed) return@onKeyEvent false
            val days = currentDates
            val cursor = state.cursor?.takeIf { it in days } ?: currentStart ?: return@onKeyEvent false
            when (event.key) {
                Key.PageUp, Key.PageDown -> {
                    val month = selection?.month ?: return@onKeyEvent false
                    state.cursor = cursor
                    state.keyboardActive = true
                    MonthCalendarFocus.request(cursor.day)
                    selection.onMonth(if (event.key == Key.PageUp) month.minusMonth() else month.plusMonth())
                    true
                }
                Key.Enter, Key.NumPadEnter -> {
                    state.cursor = cursor
                    state.keyboardActive = true
                    current(cursor)
                    true
                }
                else -> {
                    val next = stepCalendarDay(days, cursor, event.key, firstDayOfWeek) ?: return@onKeyEvent false
                    state.cursor = next
                    state.keyboardActive = true
                    true
                }
            }
        }
        .semantics { contentDescription = label }
        .onFocusChanged { focus ->
            val gained = focus.isFocused && !state.focused
            state.focused = focus.isFocused
            // Only Tab (or a key) focuses the calendar itself: the ring shows at once.
            if (gained) {
                state.keyboardActive = true
                if (state.cursor !in currentDates) state.cursor = currentStart
            }
        }
        .focusRequester(state.focusRequester)
        .focusable()
        .testTag(MonthCalendarTags.CALENDAR)
}

/** A day of a [monthCalendarKeys] calendar: clickable, but not a Tab stop of its own. */
fun Modifier.monthCalendarDay(date: LocalDate): Modifier =
    this.focusProperties { canFocus = false }.testTag(MonthCalendarTags.day(date))

/** The keyboard's ring around a day's circle of [size], as the roster grid's (navy, a white inner line). */
@Composable
fun BoxScope.CalendarFocusRing(size: Dp) {
    Box(
        Modifier.align(Alignment.Center).size(size)
            .border(2.5.dp, DeepNavy, CircleShape)
            .padding(2.5.dp)
            .border(1.dp, Color.White, CircleShape)
            .testTag(MonthCalendarTags.FOCUS_RING)
    )
}
